/* eslint-disable react/jsx-key */
import React from 'react';
import { Dialog, DialogActions, DialogTitle } from '@mui/material';
import Button from '@mui/material/Button';
import moment from 'moment';
import { FormattedMessage, injectIntl } from 'react-intl';

import * as FileSaver from 'file-saver';
import XLSX from 'sheetjs-style';

import { getWeekFromDateString, formatProductVolume, chainTypeToString } from 'services/Utils';

import ProductPricesTable from '../../../../components/products/pricetable/ProductPricesTable';

const contentStyle = {
  width: '80%',
  maxWidth: 'none',
};

const priceSortComparator = (v1, v2) => {
  const n1 = parseFloat(v1);
  const n2 = parseFloat(v2);
  if (isNaN(n1) && isNaN(n2)) return 0;
  if (isNaN(n1)) return -1;
  if (isNaN(n2)) return 1;
  return n1 - n2;
};

const stringSortComparator = (v1, v2) => {
  const s1 = v1 ? String(v1).trim() : '';
  const s2 = v2 ? String(v2).trim() : '';
  return s1.localeCompare(s2, 'sv');
};

const getExportColumns = (intl) => [
  {
    field: 'date',
    headerName: intl.formatMessage({ id: 'priceTable.date' }),
    flex: 1,
    excelWidth: 10,
    filterable: false,
  },
  {
    field: 'week',
    headerName: intl.formatMessage({ id: 'priceTable.week' }),
    flex: 0,
    excelWidth: 10,
    filterable: false,
  },
  {
    field: 'chain',
    headerName: intl.formatMessage({ id: 'priceTable.chain' }),
    flex: 1,
    excelWidth: 10,
    sortComparator: stringSortComparator,
  },
  {
    field: 'store',
    headerName: intl.formatMessage({ id: 'priceTable.store' }),
    flex: 3,
    excelWidth: 30,
    filterable: false,
    sortComparator: stringSortComparator,
  },
  {
    field: 'product',
    headerName: intl.formatMessage({ id: 'priceTable.product' }),
    flex: 2,
    excelWidth: 30,
    sortComparator: stringSortComparator,
  },
  {
    field: 'manufacturer',
    headerName: intl.formatMessage({ id: 'priceTable.brand' }),
    flex: 1,
    excelWidth: 20,
    filterable: false,
    sortComparator: stringSortComparator,
  },
  {
    field: 'gtin',
    headerName: intl.formatMessage({ id: 'priceTable.gtin' }),
    flex: 2,
    excelWidth: 20,
    filterable: false,
    sortComparator: stringSortComparator,
  },
  {
    field: 'volume',
    headerName: intl.formatMessage({ id: 'priceTable.volume' }),
    flex: 1,
    excelWidth: 10,
    filterable: false,
    sortComparator: stringSortComparator,
  },
  {
    field: 'price',
    headerName: intl.formatMessage({ id: 'priceTable.regularPrice' }),
    flex: 1,
    excelWidth: 10,
    t: 'n',
    z: '0.00',
    filterable: false,
    sortComparator: priceSortComparator,
  },
  {
    field: 'discountAmount',
    headerName: intl.formatMessage({ id: 'priceTable.campaignPrice' }),
    flex: 1,
    excelWidth: 10,
    t: 'n',
    z: '0.00',
    filterable: false,
    sortComparator: priceSortComparator,
  },
];

class PriceTableDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      rows: [],
      chainFilter: '',
      productFilter: '',
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.products !== this.props.products) {
      let rows = [];
      const products = this.props.products;
      if (products || products.length) {
        products.forEach((product) => {
          rows = [...rows, ...this.createRows(product)];
        });
      }

      this.setState({ rows });
      this.forceUpdate();
    }
  }

  handleClose() {
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  handleChainFilterChange = (event) => {
    this.setState({ chainFilter: event.target.value });
  };

  handleProductFilterChange = (event) => {
    this.setState({ productFilter: event.target.value });
  };

  getFilteredRows() {
    const { rows, chainFilter, productFilter } = this.state;
    return rows.filter((r) => {
      const matchChain = chainFilter ? r.chain === chainFilter : true;
      const matchProduct = productFilter ? r.productKey === productFilter : true;
      return matchChain && matchProduct;
    });
  }

  createRows(product) {
    const rows = [];
    const stores = product.getActiveStores();

    const startDate = this.props.startDate ? moment(this.props.startDate).startOf('day') : null;
    const endDate = this.props.endDate ? moment(this.props.endDate).endOf('day') : null;

    stores.forEach((store) => {
      const prices = store.getProductPrices(product.id);
      let chain = store.chainName || this.props.intl.formatMessage({ id: "store.other" });
      let storeName = store.name;
      if (chainTypeToString(store.type) != chain) {
        if (chain == 'MatHem') {
          chain = store.chainName;
        } else if (chainTypeToString(store.type) == 'Online') {
          chain = store.chainName;
          storeName += ' (Online)';
        } else {
          chain += ' ' + chainTypeToString(store.type);
        }
      }
      storeName = (chain + ' ' + storeName).trim();
      prices.map((innerPrice) => {
        innerPrice.forEach((price) => {
          const priceDate = moment(price.date);
          const lastDate = price.lastDate ? moment(price.lastDate) : null;

          // Ignore if price ended before the selected start date
          if (startDate && lastDate && lastDate.isBefore(startDate)) return;

          // Ignore if price started after the selected end date
          if (endDate && priceDate.isAfter(endDate)) return;

          // Bound the display date to the selected start date
          let displayDate = priceDate;
          if (startDate && priceDate.isBefore(startDate)) {
            displayDate = startDate;
          }

          const date = this.formatDate(displayDate);
          rows.push({
            date,
            chain: chain,
            store: storeName,
            product: product.name,
            productKey: product.id,
            price: this.formatAmountToPrice(price.amount),
            discountAmount: this.formatAmountToPrice(price.discountAmount),
            volume: formatProductVolume(product.amountOfUnits, product.unit),
            gtin: product.gtin,
            manufacturer: product.varumarke,
            week: getWeekFromDateString(date),
          });
        });
      });
    });

    return rows;
  }

  formatDate(date) {
    return moment(date).format('YYYY-MM-DD');
  }

  formatAmountToPrice(amount) {
    if (amount) {
      const priceKr = amount / 100;
      return priceKr.toFixed(2);
    }
    return '';
  }

  exportToExcel() {
    const exportColumns = getExportColumns(this.props.intl);
    const filteredRows = this.getFilteredRows();
    const excelData = filteredRows.map((r) => {
      const rr = {};
      exportColumns.forEach((c) => {
        rr[c.headerName] = r[c.field];
      });
      return rr;
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    const charIntA = 65;

    // set bold headers
    for (let i = 0; i < exportColumns.length; i++) {
      const col = `${String.fromCharCode(i + charIntA)}`;
      ws[`${col}1`].s = { font: { bold: true } };
      var cellType = exportColumns[i].t;
      var cellFormat = exportColumns[i].z;
      if (cellType) {
        for (let j = 0; j < excelData.length; j++) {
          const cell = `${col}${j + 2}`;
          ws[cell].t = cellType;
          if (cellFormat) {
            ws[cell].z = cellFormat;
          }
        }
      }
    }

    // set column widths
    var wscols = exportColumns.map((c) => {
      return { wch: c.excelWidth };
    });
    ws['!cols'] = wscols;

    const sheetName = this.props.intl.formatMessage({ id: 'priceTable.title' });
    const wb = { Sheets: { [sheetName]: ws }, SheetNames: [sheetName] };

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmI formats-officedocument. spreadsheetmI. sheet; charset-UTF-8' });
    FileSaver.saveAs(data, `${sheetName}.xlsx`);
  }

  render() {
    const filteredRows = this.getFilteredRows();
    return (
      <Dialog fullWidth maxWidth={'xl'} open={this.props.open || false} onClose={this.handleClose.bind(this)} scroll={'paper'}>
        <DialogTitle>
          <FormattedMessage id="priceTable.title" />
        </DialogTitle>
        <ProductPricesTable
          columns={getExportColumns(this.props.intl)}
          products={filteredRows}
          unfilteredProducts={this.state.rows}
          chainFilter={this.state.chainFilter}
          productFilter={this.state.productFilter}
          onChainFilterChange={this.handleChainFilterChange}
          onProductFilterChange={this.handleProductFilterChange}
          height={'calc(100vh - 200px)'}
        />
        <DialogActions>
          <Button color="primary" onClick={this.exportToExcel.bind(this)}>
            <FormattedMessage id="common.export" />
          </Button>

          <Button primary onClick={this.handleClose.bind(this)}>
            <FormattedMessage id="common.close" />
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default injectIntl(PriceTableDialog);
